import {
  Component,
  ViewChild,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TemplateService } from '../../../../services/template.service';
import { GlobalTemplateService } from '../../../../services/global-template.service';
import { Subject, takeUntil } from 'rxjs';
import { TemporaryMedia } from '../../../../models/email-template/temporary-media.interface';
import { getUploadErrorMessage } from '../../../../utils/upload-error-helper';
import { TypeEnum } from '../../../../models/template/type.enum';
import { META_CAROUSEL_MAX_VIDEO_SECONDS, metaCarouselDimensionError } from '../../../../utils/meta-carousel-media-rules';

interface Dimension {
  width: number;
  height: number;
  /** seconds; videos only */
  duration?: number;
}

const MAX_FILE_SIZE_BYTES = 4 * 1024 ** 3; // 4 GB, Meta's limit
const ALLOWED_EXTENSIONS = ['mp4', 'mov', 'gif'];
const META_VIDEO_DIMENSIONS: Dimension[] = [
  { width: 1440, height: 1440 },
  { width: 1440, height: 1800 }
];

@Component({
    selector: 'app-video-upload',
    imports: [CommonModule],
    templateUrl: './video-upload.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./video-upload.component.scss']
})
export class VideoUploadComponent implements OnInit, OnDestroy {
  constructor(
    private templateService: TemplateService,
    private globalTemplateService: GlobalTemplateService
  ) {
  }

  ngOnInit(): void {
    // Get template name
    this.globalTemplateService.templateNameData$
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: string) => {
        this.templateName = res;
      });

    // Get template ID
    this.globalTemplateService.templateIdData$
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: string) => {
        this.templateId = res;
      });

    // Get info if the template is new
    this.globalTemplateService.isNewTemplateData$
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: boolean) => {
        this.isTemplateNew = res;
      });

    // Get template type
    this.globalTemplateService.templateTypeData$
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: TypeEnum) => {
        this.templateType = res;
      });

    // Subscribe to temporary media array to see any new media has been uploaded
    this.updateVideoSource();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  @ViewChild('fileInput', { static: true })
  fileInput!: ElementRef<HTMLInputElement>;
  @Output() videoSrcEvent = new EventEmitter<string>();
  @Input() videoSrc: string | undefined;
  @Input() blockId: string | undefined;
  templateName: string | undefined;
  templateId: string | undefined;
  isTemplateNew: boolean = false;
  temporaryMedia: TemporaryMedia[] | undefined;
  private _destroy$ = new Subject<void>();
  templateType: TypeEnum | undefined;

  /** Shown inline next to the drop zone - cleared on every new attempt. */
  protected uploadError: string | null = null;

  /** Whether we are currently uploading a video. */
  protected isUploading = false;

  /**
   * When true, the <video> element failed to play the source (e.g. a GIF),
   * so we fall back to showing it as an <img> instead.
   */
  protected showAsImage = false;

  /**
   * Subscribes to the `temporaryMediaData$` observable from `globalTemplateService`
   * and updates the `temporaryMedia` property with the latest temporary media.
   * If the component is handling a new template (`isTemplateNew === true`) and a `templateId` is present,
   * it checks if the current `videoSrc` matches any temporary media URI.
   * If a match is found and the temporary media item has a `newMediaURI`, it updates `videoSrc` accordingly.
   *
   * @private
   */
  private updateVideoSource() {
    this.globalTemplateService.temporaryMediaData$
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: TemporaryMedia[]) => {
        this.temporaryMedia = res;

        // Check if videoSrc has changed
        if (this.templateId && this.isTemplateNew === true) {
          const newVideo = this.temporaryMedia.find(
            (tempMedia) => tempMedia.tempMediaURI === this.videoSrc
          );
          if (newVideo && newVideo.newMediaURI) {
            this.videoSrc = newVideo.newMediaURI;
          }
        }
      });
  }

  /**
   * Emits the current video source (`videoSrc`) through the `videoSrcEvent` EventEmitter.
   * This method should be called whenever the video source changes and needs to be communicated
   * to parent components or other listeners.
   */
  emitVideoSrc() {
    this.videoSrcEvent.emit(this.videoSrc);
  }

  /**
   * Handles the dragover event for the video upload component.
   *
   * @param event - The drag event triggered when an item is dragged over the drop zone.
   */
  onDragOver(event: DragEvent) {
    // Prevents the default browser behavior to allow custom drag-and-drop functionality.
    event.preventDefault();
  }

  /**
   * Handles the drop event for file uploads.
   * Prevents the default browser behavior and, if a file is present in the dropped data,
   * initiates loading of the first file using the `loadFile` method.
   *
   * @param event - The drag event containing the dropped files.
   */
  onDrop(event: DragEvent) {
    // Prevents the default browser behavior to allow custom drag-and-drop functionality.
    event.preventDefault();

    this.onDropForwardFile(event);
  }

  /**
   * Handles the video change event when a new video file is selected.
   * If the template is new, loads the video and sets temporary media data in the global template service.
   * If the template is not new, uploads the video to Azure Storage immediately.
   *
   * @param file - The selected video file to be processed.
   */
  async onVideoChange(file: File | undefined) {
    this.uploadError = null;

    // A drop that carried no file (e.g. dragged text) has nothing to validate.
    if (!file) {
      return;
    }

    if (!(await this.isVideoCompliant(file))) {
      return;
    }

    if (this.isTemplateNew) {
      this.videoSrc = URL.createObjectURL(file);
      this.showAsImage = false;
      this.emitVideoSrc();
      this.globalTemplateService.setTemporaryMediaData({
        tempMediaURI: this.videoSrc,
        file: file,
        blockId: this.blockId!,
        kind: 'video'
      });
    } else {
      this.uploadVideoWhenOld(file);
    }
  }

  /**
   * Validates the video file against allowed extensions, size, and dimensions.
   *
   * @param file - The video file to validate.
   * @returns A promise resolving to `true` if the video is compliant.
   */
  private async isVideoCompliant(file: File): Promise<boolean> {
    // 1. Check extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
      this.uploadError = "File type must be MP4, MOV or GIF!";
      return false;
    }

    // 2. Check file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.uploadError = "File size is larger than 4 GB!";
      return false;
    }

    // 3. Check dimensions
    let dimensions: Dimension;
    try {
      if (extension === 'gif') {
        dimensions = await this.getGifDimensions(file);
      } else {
        dimensions = await this.getVideoDimensions(file);
      }
    } catch {
      if (extension === 'gif') {
        this.uploadError = "Could not read the GIF file!";
      } else {
        this.uploadError = "Could not read the video — use an H.264 MP4 or MOV.";
      }
      return false;
    }

    if (this.templateType === TypeEnum.META_CAROUSEL) {
      const dimensionError = metaCarouselDimensionError('Video', dimensions.width, dimensions.height);
      if (dimensionError) {
        this.uploadError = dimensionError;
        return false;
      }
      // A GIF has no readable duration; Meta caps carousel videos at 240 minutes.
      if (dimensions.duration !== undefined && dimensions.duration > META_CAROUSEL_MAX_VIDEO_SECONDS) {
        this.uploadError = "Video must be at most 240 minutes long!";
        return false;
      }
      return true;
    }

    const matches = META_VIDEO_DIMENSIONS.some(
      (d) => d.width === dimensions.width && d.height === dimensions.height
    );
    if (!matches) {
      this.uploadError = "Video must be exactly 1440x1440 (1:1) or 1440x1800 (4:5)!";
      return false;
    }

    return true;
  }

  /**
   * Reads the pixel dimensions of a GIF file.
   *
   * @param file - The GIF file to measure.
   * @returns A promise resolving to the GIF's width and height in pixels.
   */
  private getGifDimensions(file: File): Promise<Dimension> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Could not read GIF'));
      };
      img.src = url;
    });
  }

  /**
   * Reads the pixel dimensions and duration of a video file.
   *
   * @param file - The video file to measure.
   * @returns A promise resolving to the video's width and height in pixels and its duration in seconds.
   */
  private getVideoDimensions(file: File): Promise<Dimension> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve({ width: video.videoWidth, height: video.videoHeight, duration: video.duration });
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Could not read video'));
      };
      video.src = url;
    });
  }

  /**
   * Handles the video upload process when the template is not new.
   *
   * If the template is old, videos can be directly uploaded to Azure Blob storage.
   *
   * @param file - The selected video file to upload.
   */
  private uploadVideoWhenOld(file: File) {
    const previousSrc = this.videoSrc;
    this.isUploading = true;

    this.templateService
      .uploadVideo(file, this.templateName!, this.blockId!)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res) => {
          this.isUploading = false;
          if (previousSrc) {
            this.globalTemplateService.setMediaForDeletionData(previousSrc);
          }
          this.videoSrc = res;
          this.showAsImage = false;
          this.emitVideoSrc();
        },
        error: (err) => {
          this.isUploading = false;
          console.error('Video upload failed:', err);
          this.uploadError = getUploadErrorMessage(err, 'Video upload failed!');
        }
      });
  }

  /**
   * Handles the drop event for forwarding a file.
   * Extracts the first file from the drag event and passes it to the video change handler.
   *
   * @param event - The drag event containing the dropped files.
   */
  private onDropForwardFile(event: DragEvent) {
    this.onVideoChange(event.dataTransfer!.files[0]);
  }

  /**
   * Handles the click event for forwarding a file input.
   * Extracts the selected file from the event's target and passes it to the `onVideoChange` handler.
   *
   * @param event - The click event triggered by the file input element.
   */
  onClickForwardFile(event: Event) {
    const input = event.target as HTMLInputElement;
    this.onVideoChange(input.files![0]);

    // Reset the input so selecting the same file again still fires `change`.
    // Without this, re-picking an identical file is a no-op and validation
    // (size, extension, dimensions) never re-runs.
    input.value = '';
  }

  /**
   * Opens the native file selection dialog by programmatically triggering a click event
   * on the file input element referenced by `fileInput`.
   *
   * This method is typically used to allow users to select files for upload without
   * directly interacting with the file input element in the UI.
   */
  openFileDialog() {
    this.fileInput.nativeElement.click();
  }

  /**
   * Called when the <video> element fails to play the source (e.g. a GIF).
   * Switches the preview to show the video as an image instead.
   */
  protected onPreviewError(): void {
    this.showAsImage = true;
  }
}
